/**
 * 用户模块 API 服务
 * 需要登录认证
 */

import type {
  SecurityFields,
  UserInfo,
  UpdateUserInfoRequest,
  BindAuthRequest,
  InviteInfo,
  TeamStats,
  VipLevelInfo,
  VipLevelConfig,
  VipRebateRateItem,
  ListVipRebateRatesData,
  VipRewardItem,
  ListVipRewardsData,
  ClaimVipRewardRequest,
  ClaimVipRewardData,
  FundPasswordStatusData,
  SetFundPasswordRequest,
  UpdateFundPasswordRequest,
} from '@/types/api'
import { post } from '@/services/request'

/**
 * 获取用户信息
 */
export async function getUserInfo(): Promise<{
  success: boolean
  data?: UserInfo
  error?: string
}> {
  try {
    const response = await post<object, UserInfo>('/api/v1/user/info', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取用户信息失败' }
  } catch (error) {
    console.error('Get user info error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 更新用户信息
 */
export async function updateUserInfo(data: Omit<UpdateUserInfoRequest, keyof SecurityFields>): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<typeof data, object>('/api/v1/user/update', data, { auth: true })

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '更新用户信息失败' }
  } catch (error) {
    console.error('Update user info error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 绑定认证方式
 */
export async function bindAuth(data: Omit<BindAuthRequest, keyof SecurityFields>): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<typeof data, object>('/api/v1/user/bind', data, { auth: true })

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '绑定失败' }
  } catch (error) {
    console.error('Bind auth error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取邀请信息
 */
export async function getInviteInfo(): Promise<{
  success: boolean
  data?: InviteInfo
  error?: string
}> {
  try {
    const response = await post<object, InviteInfo>('/api/v1/user/invite', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取邀请信息失败' }
  } catch (error) {
    console.error('Get invite info error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取团队统计
 */
export async function getTeamStats(): Promise<{
  success: boolean
  data?: TeamStats
  error?: string
}> {
  try {
    const response = await post<object, TeamStats>('/api/v1/user/team', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取团队统计失败' }
  } catch (error) {
    console.error('Get team stats error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取 VIP 等级信息
 */
export async function getVipLevel(): Promise<{
  success: boolean
  data?: VipLevelInfo
  error?: string
}> {
  try {
    const response = await post<object, VipLevelInfo>('/api/v1/user/vip', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取VIP等级失败' }
  } catch (error) {
    console.error('Get VIP level error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取 VIP 等级列表
 */
export async function getVipLevels(): Promise<{
  success: boolean
  data?: VipLevelConfig[]
  error?: string
}> {
  try {
    const response = await post<object, { items: VipLevelConfig[] }>('/api/v1/user/vip/levels', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items }
    }

    return { success: false, error: response.msg || '获取VIP等级列表失败' }
  } catch (error) {
    console.error('Get VIP levels error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取 VIP 反水比例表
 * 注意: swagger 未定义 data 结构, 字段以实测为准
 */
export async function getVipRebateRates(): Promise<{
  success: boolean
  data?: VipRebateRateItem[]
  error?: string
}> {
  try {
    const response = await post<object, ListVipRebateRatesData>('/api/v1/user/vip/rebate-rates', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items ?? [] }
    }

    return { success: false, error: response.msg || '获取VIP反水比例失败' }
  } catch (error) {
    console.error('Get VIP rebate-rates error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取 VIP 奖励列表
 */
export async function getVipRewards(): Promise<{
  success: boolean
  data?: VipRewardItem[]
  error?: string
}> {
  try {
    const response = await post<object, ListVipRewardsData>('/api/v1/vip/rewards', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items ?? [] }
    }

    return { success: false, error: response.msg || '获取VIP奖励列表失败' }
  } catch (error) {
    console.error('Get VIP rewards error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 领取 VIP 奖励
 */
export async function claimVipReward(rewardId: number): Promise<{
  success: boolean
  data?: ClaimVipRewardData
  error?: string
}> {
  try {
    const response = await post<ClaimVipRewardRequest, ClaimVipRewardData>(
      '/api/v1/vip/reward/claim',
      { reward_id: rewardId },
      { auth: true }
    )

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '领取VIP奖励失败' }
  } catch (error) {
    console.error('Claim VIP reward error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 用户全局心跳（维护在线状态）
 * 前端在整个 App 生命周期内每 30 秒调用一次
 */
export async function userHeartbeat(): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<object, object>('/api/v1/user/heartbeat', {}, { auth: true })

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '心跳失败' }
  } catch (error) {
    console.error('User heartbeat error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取资金密码状态
 */
export async function getFundPasswordStatus(): Promise<{
  success: boolean
  data?: FundPasswordStatusData
  error?: string
}> {
  try {
    const response = await post<object, FundPasswordStatusData>('/api/v1/fund-password/status', {}, { auth: true })

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取资金密码状态失败' }
  } catch (error) {
    console.error('Get fund password status error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 设置资金密码（首次设置）
 */
export async function setFundPassword(password: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<Omit<SetFundPasswordRequest, keyof SecurityFields>, object>(
      '/api/v1/fund-password/set',
      { password },
      { auth: true }
    )

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '设置资金密码失败' }
  } catch (error) {
    console.error('Set fund password error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 更新资金密码
 */
export async function updateFundPassword(oldPassword: string, newPassword: string): Promise<{
  success: boolean
  error?: string
}> {
  try {
    const response = await post<Omit<UpdateFundPasswordRequest, keyof SecurityFields>, object>(
      '/api/v1/fund-password/update',
      { old_password: oldPassword, new_password: newPassword },
      { auth: true }
    )

    if (response.code === 0) {
      return { success: true }
    }

    return { success: false, error: response.msg || '更新资金密码失败' }
  } catch (error) {
    console.error('Update fund password error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}
